import { create, StateCreator } from 'zustand';
import { persist } from 'zustand/middleware';

type LastPriceEntity = {
  price: string;
  isLoading: boolean;
};

type LastPriceState = {
  entities: Record<number, LastPriceEntity>;
};

type LastPriceActions = {
  update: (entityId: number, entity: Partial<LastPriceEntity>) => void;
  setLoading: (entityId: number, isLoading: boolean) => void;
  getPriceOrFallback: (entityId: Optional<number>) => LastPriceEntity;
};

const FALLBACK_LAST_PRICE_ENTITY: LastPriceEntity = {
  price: '0',
  isLoading: false,
};

const lastPriceSlice: StateCreator<LastPriceState & LastPriceActions> = (set, get) => ({
  // Initial state
  entities: {},

  // Actions
  update: (entityId: number, entity: Partial<LastPriceEntity>) => {
    return set(state => {
      const existingEntity = state.entities[entityId];
      const newPrice =
        entity?.price === ''
          ? (existingEntity?.price ?? '0') // Empty string: preserve existing or use '0'
          : (entity?.price ?? '0'); // Use provided value or fallback to '0'

      return {
        entities: {
          ...state.entities,
          [entityId]: { price: newPrice, isLoading: entity?.isLoading ?? false },
        },
      };
    });
  },

  setLoading: (entityId: number, isLoading: boolean) =>
    set(state => ({
      entities: {
        ...state.entities,
        [entityId]: { ...state.entities[entityId], isLoading },
      },
    })),

  getPriceOrFallback: (entityId: Optional<number>) => {
    if (!entityId) return FALLBACK_LAST_PRICE_ENTITY;

    const state = get();
    const entity = state.entities?.[entityId];
    return entity ?? FALLBACK_LAST_PRICE_ENTITY;
  },
});

const persistedLastPriceSliceStore = persist<LastPriceState & LastPriceActions>(lastPriceSlice, {
  name: `${window.location.origin}/last-price`,
  onRehydrateStorage: () => state => {
    // Set isLoading to true for all cached entities since they're potentially outdated
    if (state?.entities) {
      Object.keys(state.entities).forEach(entityId => {
        const numericId = Number(entityId);
        if (state.entities[numericId]) {
          state.entities[numericId].isLoading = true;
        }
      });
    }
  },
});

export const useLastPriceStore = create(persistedLastPriceSliceStore);
